import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Collapse,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Drawer,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

const KitchenAmenities = ({ kitchen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openPopup, setOpenPopup] = useState(false);

  const defaultAmenities = [
    {
      category: "Food Preparation",
      items: ["Food Processor", "Food Scales", "Mixer (Stand or Hand)"],
    },
    {
      category: "Cooking Equipment",
      items: [
        "Commercial Oven (Double)",
        "Deep Fryer",
        "Microwave",
        "Range/Stove with 6 Burners",
      ],
    },
    {
      category: "Dishwashing and Cleaning",
      items: ["Dish Drying Racks", "Sinks (with 3 compartments)"],
    },
    {
      category: "Refrigeration and Cooling",
      items: ["Ice Machine", "Walk-in Refrigerator"],
    },
  ];

  let amenities = [];
  const rawAmenities = kitchen?.Amenities;

  useEffect(() => {
    console.log("kitchen.Amenities:", rawAmenities); // Debug log
  }, [kitchen]);

  if (Array.isArray(rawAmenities)) {
    amenities = [{ category: "What this place offers", items: rawAmenities }];
  } else if (typeof rawAmenities === "string") {
    amenities = [
      {
        category: "What this place offers",
        items: rawAmenities.split(",").map((item) => item.trim()),
      },
    ];
  } else if (
    rawAmenities &&
    typeof rawAmenities === "object" &&
    !Array.isArray(rawAmenities)
  ) {
    amenities = Object.entries(rawAmenities)
      .filter(([key]) => key && !key.includes("&")) // Filter out invalid keys
      .map(([category, items]) => ({
        category,
        items: Array.isArray(items) ? items : [],
      }));
  } else {
    amenities = defaultAmenities;
  }

  amenities = amenities.filter((cat) => cat.items.length > 0);

  const totalItems = amenities.reduce((acc, cat) => acc + cat.items.length, 0);
  const initialCount = isMobile ? 4 : 6;
  const visibleItems = [];
  let itemCount = 0;

  for (const category of amenities) {
    for (const item of category.items) {
      if (itemCount < initialCount) {
        visibleItems.push({ category: category.category, item });
        itemCount++;
      } else {
        break;
      }
    }
  }

  const handleOpenPopup = () => setOpenPopup(true);
  const handleClosePopup = () => setOpenPopup(false);

  return (
    <Box sx={{ p: 0, m: 0 }}>
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        color="text.primary"
        fontWeight={600}
        sx={{
          mt: 0.5,
          mb: 0,
          bgcolor: "background.paper",
          "&": {
            marginTop: "4px !important",
            marginBottom: "0 !important",
          },
        }}
      >
        What this place offers
      </Typography>
      <Divider
        sx={{
          mt: 0,
          mb: 0,
          bgcolor: "divider",
          opacity: 0.3,
          "&": {
            marginTop: "8px !important",
            marginBottom: "8px !important",
          },
        }}
      />
      {!isMobile ? (
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <List disablePadding>
              <Collapse in={true} timeout={400}>
                <List disablePadding>
                  {visibleItems.slice(0, 3).map((itemObj, index) => (
                    <ListItem
                      key={index}
                      disableGutters
                      sx={{ py: 0.25, pl: 1 }}
                    >
                      <ListItemText
                        primary={`• ${itemObj.item}`}
                        primaryTypographyProps={{
                          variant: isMobile ? "body2" : "body1",
                          color: "text.secondary",
                          sx: { lineHeight: 1.4, ml: 0.5, fontWeight: 400 },
                          "&": {
                            marginTop: "8px !important",
                            marginBottom: "8px !important",
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List disablePadding>
              <Collapse in={true} timeout={400}>
                <List disablePadding>
                  {visibleItems.slice(3, 6).map((itemObj, index) => (
                    <ListItem
                      key={index}
                      disableGutters
                      sx={{ py: 0.25, pl: 1 }}
                    >
                      <ListItemText
                        primary={`• ${itemObj.item}`}
                        primaryTypographyProps={{
                          variant: isMobile ? "body2" : "body1",
                          color: "text.secondary",
                          sx: { lineHeight: 1.4, ml: 0.5, fontWeight: 400 },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </List>
          </Grid>
        </Grid>
      ) : (
        <List disablePadding>
          <Collapse in={true} timeout={400}>
            <List disablePadding>
              {visibleItems.slice(0, 4).map((itemObj, index) => (
                <ListItem key={index} disableGutters sx={{ py: 0.25, pl: 1 }}>
                  <ListItemText
                    primary={`• ${itemObj.item}`}
                    primaryTypographyProps={{
                      variant: isMobile ? "body2" : "body1",
                      color: "text.secondary",
                      sx: { lineHeight: 1.4, ml: 0.5, fontWeight: 400 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </List>
      )}
      {totalItems > (isMobile ? 4 : 6) && (
        <Button
          variant="text"
          onClick={handleOpenPopup}
          sx={{
            mt: 1,
            color: theme.palette.error.main,
            textTransform: "none",
            fontSize: isMobile ? "0.875rem" : "1rem",
            borderRadius: 16,
            padding: "4px 12px",
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.04)",
              color: theme.palette.error.dark,
            },
          }}
          aria-label="Show all amenities"
        >
          {`Show all ${totalItems} amenities`}
        </Button>
      )}
      {!isMobile && (
        <Dialog
          open={openPopup}
          onClose={handleClosePopup}
          maxWidth="md"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: 1,
              maxHeight: "80vh",
              bgcolor: "background.paper",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              All Amenities
            </Typography>
            <IconButton onClick={handleClosePopup} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ maxHeight: "60vh", overflowY: "auto" }}>
            {amenities.map((category, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
                  {category.category}
                </Typography>
                <List disablePadding>
                  {category.items.map((item, itemIndex) => (
                    <ListItem
                      key={itemIndex}
                      disableGutters
                      sx={{ py: 0.25, pl: 1 }}
                    >
                      <ListItemText
                        primary={`• ${item}`}
                        primaryTypographyProps={{
                          variant: "body1",
                          color: "text.secondary",
                          sx: { lineHeight: 1.4, ml: 0.5, fontWeight: 400 },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </DialogContent>
        </Dialog>
      )}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={openPopup}
          onClose={handleClosePopup}
          sx={{
            "& .MuiDrawer-paper": {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              bgcolor: "background.paper",
              maxHeight: "80vh",
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                All Amenities
              </Typography>
              <IconButton onClick={handleClosePopup} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
              {amenities.map((category, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    sx={{ mb: 1 }}
                  >
                    {category.category}
                  </Typography>
                  <List disablePadding>
                    {category.items.map((item, itemIndex) => (
                      <ListItem
                        key={itemIndex}
                        disableGutters
                        sx={{ py: 0.25, pl: 1 }}
                      >
                        <ListItemText
                          primary={`• ${item}`}
                          primaryTypographyProps={{
                            variant: "body2",
                            color: "text.secondary",
                            sx: { lineHeight: 1.4, ml: 0.5, fontWeight: 400 },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </Box>
          </Box>
        </Drawer>
      )}
      <Box sx={{ mb: 0.5 }} />
    </Box>
  );
};

export default KitchenAmenities;
